import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * RouteAnnouncer — accessibility helper for the client-side (SPA) router.
 *
 * Problem it fixes (a11y measurement A38, web-stg 2026-07-27):
 * on every in-app navigation, `document.activeElement` is silently dumped back
 * to `<body>` and nothing is announced — a keyboard/screen-reader user loses
 * their place and gets no feedback that the page changed. There was no route
 * announcer anywhere in the bundle (only the brand loader's `role=status`).
 *
 * This mirrors the well-established Gatsby / Next.js route-announcer pattern:
 * a single off-screen, `aria-live` region that, on each SPA navigation,
 *   1) announces the new page name to assistive tech, and
 *   2) moves keyboard focus onto itself (top of page, inert) so focus is not
 *      left orphaned on <body>.
 *
 * Deliberately conservative to respect the legacy app:
 *  - Skips the very first render (the fresh full-page load is announced by the
 *    browser itself; we only handle subsequent client-side transitions).
 *  - Never moves focus while a modal dialog is open, so wizard / lightbox
 *    flows that manage their own focus are untouched.
 *  - Rendered BEFORE <Routes/>, so a page that autofocuses its own control on
 *    mount runs afterwards and still wins.
 *  - Uses a deterministic route-name map (no reading of not-yet-rendered DOM),
 *    so it can never announce stale content.
 *  - Purely additive: no existing markup, styles or behaviour is changed.
 */

// Human-readable page names keyed by the first path segment.
export const ROUTE_NAMES = {
  '': 'Home',
  gallery: 'Gallery',
  // Renamed Explore -> Discover, Gordon 2026-08-06 ("the page is called
  // Discover, not Explore"). This is what a screen reader announces on every
  // SPA nav to /explore ("Discover, page loaded").
  explore: 'Discover',
  wheel: 'Wheel',
  'cool-wheel': 'Wheel',
  builder: 'Wheel builder',
  create: 'Create a wheel',
  settings: 'Settings',
  analytics: 'Analytics',
  'media-library': 'Media library',
  notifications: 'Notifications',
  chat: 'Messages',
  referrals: 'Referrals',
  help: 'Help',
  login: 'Log in',
  signup: 'Sign up',
  'forgot-password': 'Forgot password',
};

export function pageNameFor(pathname) {
  const seg = (pathname || '/').split('/').filter(Boolean)[0] || '';
  if (Object.prototype.hasOwnProperty.call(ROUTE_NAMES, seg)) return ROUTE_NAMES[seg];
  // Fallback: derive a readable label from the first path segment.
  return seg
    ? seg.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Page';
}

// Screen-reader-only: visually hidden but announced. Inline to avoid touching
// any shared token / theme file.
const srOnlyStyle = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default function RouteAnnouncer() {
  const { pathname } = useLocation();
  const ref = useRef(null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const node = ref.current;
    if (!node) return;

    const label = pageNameFor(pathname);
    node.textContent = `${label}, page loaded`;

    // Do not steal focus from a modal-managed flow.
    const modalOpen = !!document.querySelector('[role="dialog"][aria-modal="true"]');
    if (!modalOpen && typeof node.focus === 'function') {
      try {
        node.focus({ preventScroll: true });
      } catch (e) {
        node.focus();
      }
    }
  }, [pathname]);

  return (
    <div
      ref={ref}
      data-testid="route-announcer"
      aria-live="assertive"
      aria-atomic="true"
      role="status"
      tabIndex={-1}
      style={srOnlyStyle}
    />
  );
}
